import { useMemo } from 'react'

import {
  digObject, matchFilterArrayIncludes, matchFilterString, matchFilterNumber, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

const watchEntityKeys = ['providerPackages']

type ProviderPackagesFilters = {
  hidden?: boolean,
  max_price?: number,
  min_price?: number,
  provider_id?: number,
  provider_price_list_id?: number,
  service_product_id?: number,
  string?: string,
}

interface Options {
  entityKey?: string,
  filters?: ProviderPackagesFilters,
  performHttpRequests?: boolean,
  requestOptions?: {},
}

function useProviderPackages(options: Options = {}) {
  const { filters, requestOptions } = options
  const {
    hidden: filterHidden,
    max_price: filterMaxPrice,
    min_price: filterMinPrice,
    provider_id: filterProviderId,
    provider_price_list_id: filterPriceListId,
    service_product_id: filterServiceProductId,
    string,
  } = filters || {}

  const {
    updatedEntities: { providerPackages: providerPackagesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const providerPackages = useSelector(reduxState => reduxState.entities.providerPackages)

  const filteredProviderPackages = useMemo(() => {
    const filtered = Object.values(providerPackages).filter((providerPackage) => {
      const {
        hidden,
        id,
        price,
        provider_id,
        provider_price_list_ids = [],
        title,
      } = providerPackage

      const serviceProductIds = digObject(providerPackage, 'service_product_ids', [])

      const matchHidden = typeof filterHidden !== 'undefined' ? hidden === filterHidden : true
      const matchId = id.toString() === string
      // @ts-expect-error TS2365
      const matchMaxPrice = filterMaxPrice ? price <= Number(filterMaxPrice) : true
      // @ts-expect-error TS2365
      const matchMinPrice = filterMinPrice ? price >= Number(filterMinPrice) : true
      const matchProviderId = matchFilterNumber(provider_id, filterProviderId)
      const matchServiceProductId = matchFilterArrayIncludes(serviceProductIds, filterServiceProductId)
      const matchPriceListId = matchFilterArrayIncludes(provider_price_list_ids, filterPriceListId)
      const matchTitle = matchFilterString(title, string)
      const matchString = matchId || matchTitle

      return matchHidden
        && matchMaxPrice
        && matchMinPrice
        && matchPriceListId
        && matchProviderId
        && matchServiceProductId
        && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [providerPackagesUpdatedAt, JSON.stringify(filters)])

  const hasFilteredProviderPackages = !!filteredProviderPackages.length
  const filteredProviderPackagesCount = filteredProviderPackages.length

  const loadMorePayload = useLoadMore({
    loadedCount: filteredProviderPackagesCount,
    filters: {
      ...filters,
    },
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingProviderPackages } = useReduxAction(
    'providerPackages',
    'loadProviderPackages',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [performHttpRequests, filtersWithOffset],
    // @ts-expect-error TS2339
    { shouldPerformFn: ({ loading }) => performHttpRequests && !loading },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredProviderPackages,
    filteredProviderPackagesCount,
    hasFilteredProviderPackages,
    loading: loadingProviderPackages,
  }
}

export default useProviderPackages
